import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { ProductDetailsComponent } from './product-details/product-details.component';
import { ProductMediaComponent } from './product-media/product-media.component';
import { Variant } from '../../pages/product/product-page/product.types';
import { CartService } from '../../pages/cart-page/cart.service';
import { ProductDescriptionComponent } from './product-description/product-description.component';
import { SeoService } from '../../services/seo.service';
import { DataLayerService } from '../../services/data-layer.service';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  combineLatest,
  distinctUntilChanged,
  distinctUntilKeyChanged,
  filter,
} from 'rxjs';
import { ScriptService } from '../../services/script.service';
import { StateService } from '../../services/state.service';
import { ProductSafetyDescriptionComponent } from './product-safety-description/product-safety-description.component';

const trustMateUrl = 'https://trustmate.io/widget/api';
const trustMateLocaleKeys = {
  sk: 'c24621c4-659e-4cb1-a159-f707a6bd0154',
  cs: '611b8f08-8a76-4e42-8420-7ca59bab384b',
  en: '9a9b17dc-1431-4a39-bfd8-118ddf59398a',
} as Record<string, string>;

@Component({
  selector: 'app-product',
  imports: [
    ProductDetailsComponent,
    ProductMediaComponent,
    ProductDescriptionComponent,
    ProductSafetyDescriptionComponent,
  ],
  templateUrl: './product.component.html',
  styleUrls: ['./product.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductComponent implements AfterViewInit, OnDestroy {
  private readonly cart = inject(CartService);
  private readonly seo = inject(SeoService);
  private readonly dataLayer = inject(DataLayerService);
  private readonly scriptService = inject(ScriptService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly stateService = inject(StateService);
  private variantSku?: string;

  _variants: Variant[] = [];

  activeVariant: WritableSignal<Variant | undefined> = signal(undefined);
  activeVariant$ = toObservable(this.activeVariant);

  trustMate = viewChild<ElementRef>('trustMate');

  constructor() {
    effect(() => {
      const trustMate = this.trustMate();

      if (trustMate) {
        trustMate.nativeElement.id = `${trustMateLocaleKeys[this.stateService.getActiveLocale()]}`;
      }
    });
  }

  ngAfterViewInit() {
    combineLatest([
      this.activeVariant$.pipe(filter(Boolean), distinctUntilKeyChanged('id')),
      this.stateService.activeLocale$.pipe(distinctUntilChanged()),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([variant, locale]) => {
        this.dataLayer.onViewItem(variant);

        if (this.variantSku === variant.variantSku) {
          return;
        }

        if (this.variantSku) {
          this.removeTrustMate();
        }

        this.variantSku = variant.variantSku;
        const scriptSrc = `${trustMateUrl}/${trustMateLocaleKeys[locale]}/script?product=${this.variantSku}`;

        this.scriptService.createAndAppend({
          id: `trust-mate-${this.variantSku}`,
          src: scriptSrc,
        });
      });
  }

  ngOnDestroy() {
    this.removeTrustMate();
  }

  @Input({ required: true })
  set variants(v: Variant[] | undefined) {
    this._variants = v ?? [];
    this.activeVariant.set(v?.[0]);

    if (v) {
      this.seo.setForProduct(this.activeVariant());
    }
  }

  onActiveVariantChange(variant: Variant): void {
    this.activeVariant.set(variant);
    this.cart.clearError();
  }

  onAddToCart(variant: Variant): void {
    this.cart.addItem(variant, 'product page');
  }

  private removeTrustMate(): void {
    this.scriptService.remove(`trust-mate-${this.variantSku}`);
  }
}
